import { ToastContext } from "context/ToastContext";
import { useCurrentAccount } from "hooks/useCurrentAccount";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useContext, useMemo, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  useCreateServiceLineSynonymMutation,
  useDeleteServiceLineSynonymMutation,
  useGetAllServiceLineSynonymsQuery,
  useUpdateServiceLineSynonymMutation,
} from "store/queries/serviceLineSynonyms";
import styled from "styled-components";
import { IServiceLineSynonym } from "types/ServiceLineSynonym/ServiceLineSynonym";

/**
 * CLE-2341 — Service Line Synonyms admin sub-tab on PayerRules → Tools.
 *
 * Per-(account, payer) bridge between the labels in the base rate file and the
 * labels in the rules sheet. The expander uses it as a fallback when a row's
 * base-file label doesn't directly match anything in the rules sheet.
 *
 * UI field names (CLE-2323 follow-up): in the original launch the dialog labels
 * were "Source service line" / "Target service line" with helper text spelling
 * out which file each refers to. Operators kept confusing which direction was
 * which (most recently: BCBS Diagnostic Imaging case, prod 2026-06-03), so the
 * UI now uses "Base rate file label" / "Rules sheet label" everywhere. The
 * underlying API + DB fields stay `sourceServiceLine` / `targetServiceLine`.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  label {
    font-size: 0.85em;
    color: var(--gray-700);
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;

  label {
    font-size: 0.85em;
    color: var(--gray-700);
  }
`;

export function ServiceLineSynonyms({
  initialPayerId,
}: {
  initialPayerId?: number;
}) {
  const { current: toastElement } = useContext(ToastContext);
  const { accountInfo } = useCurrentAccount();
  const accountId = accountInfo?.id;

  const { data: payersData } = useGetAllPayersQuery();
  const [filterPayerId, setFilterPayerId] = useState<number | null>(
    initialPayerId ?? null
  );

  const { data, refetch, isFetching } = useGetAllServiceLineSynonymsQuery(
    accountId ? { accountId, payerId: filterPayerId ?? undefined } : null,
    { skip: !accountId }
  );

  const [createSynonym, { isLoading: isCreating }] =
    useCreateServiceLineSynonymMutation();
  const [updateSynonym, { isLoading: isUpdating }] =
    useUpdateServiceLineSynonymMutation();
  const [deleteSynonym] = useDeleteServiceLineSynonymMutation();

  const payerOptions = useMemo(
    () =>
      (payersData?.data ?? []).map((p: any) => ({
        label: p.name,
        value: p.id,
      })),
    [payersData]
  );

  const payerNameById = useMemo(
    () =>
      new Map<number, string>(
        (payersData?.data ?? []).map((p: any) => [p.id, p.name])
      ),
    [payersData]
  );

  // Add / edit dialog state
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formPayerId, setFormPayerId] = useState<number | null>(null);
  const [formSource, setFormSource] = useState("");
  const [formTarget, setFormTarget] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const openAdd = () => {
    setDialogMode("add");
    setEditingId(null);
    setFormPayerId(filterPayerId);
    setFormSource("");
    setFormTarget("");
    setFormError(null);
  };

  const openEdit = (row: IServiceLineSynonym) => {
    setDialogMode("edit");
    setEditingId(row.id);
    setFormPayerId(row.payerId);
    setFormSource(row.sourceServiceLine);
    setFormTarget(row.targetServiceLine);
    setFormError(null);
  };

  const closeDialog = () => setDialogMode(null);

  const submit = async () => {
    if (!accountId) {
      setFormError("No current account selected.");
      return;
    }
    if (!formPayerId) {
      setFormError("Payer is required.");
      return;
    }
    if (!formSource.trim()) {
      setFormError("Base rate file label is required.");
      return;
    }
    if (!formTarget.trim()) {
      setFormError("Rules sheet label is required.");
      return;
    }
    try {
      if (dialogMode === "add") {
        await createSynonym({
          accountId,
          payerId: formPayerId,
          sourceServiceLine: formSource.trim(),
          targetServiceLine: formTarget.trim(),
        }).unwrap();
        toastElement?.show({
          severity: "success",
          summary: "Added",
          detail: "Synonym created.",
        });
      } else if (dialogMode === "edit" && editingId) {
        await updateSynonym({
          id: editingId,
          body: {
            sourceServiceLine: formSource.trim(),
            targetServiceLine: formTarget.trim(),
          },
        }).unwrap();
        toastElement?.show({
          severity: "success",
          summary: "Updated",
          detail: "Synonym updated.",
        });
      }
      closeDialog();
      refetch();
    } catch (e: any) {
      const detail =
        e?.data?.message ??
        e?.data?.error ??
        (typeof e?.data === "string" ? e.data : null) ??
        e?.message ??
        "Save failed.";
      setFormError(detail);
    }
  };

  const handleDelete = (row: IServiceLineSynonym) => {
    confirmDialog({
      message: `Delete synonym "${row.sourceServiceLine}" → "${row.targetServiceLine}"?`,
      header: "Delete synonym",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteSynonym(row.id).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Deleted",
            detail: "Synonym removed.",
          });
          refetch();
        } catch (e: any) {
          toastElement?.show({
            severity: "error",
            summary: "Delete failed",
            detail: e?.data?.message ?? e?.message ?? "Unknown error",
          });
        }
      },
    });
  };

  const rows = (data?.data ?? []) as IServiceLineSynonym[];

  // Extract the DataTable body renderers as variables (rather than inline arrow
  // functions in the JSX) so `react/no-unstable-nested-components` doesn't treat
  // them as freshly-defined components on every render.
  const payerCellBody = (r: IServiceLineSynonym) =>
    payerNameById.get(r.payerId) ?? r.payerId;

  const actionsCellBody = (r: IServiceLineSynonym) => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-text"
        onClick={() => openEdit(r)}
        aria-label="Edit"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-text p-button-danger"
        onClick={() => handleDelete(r)}
        aria-label="Delete"
      />
    </div>
  );

  return (
    <Wrapper>
      <h3>Service Line Synonyms</h3>
      <p style={{ margin: 0, color: "var(--gray-600)" }}>
        Bridge vocabulary mismatches between your{" "}
        <strong>base rate file</strong> and your <strong>rules sheet</strong>.
        For each mismatch, add an entry whose left side is the label as it
        appears in the rate file and whose right side is the label as it appears
        in the rules sheet. Scoped per (current account, payer). Read by the
        rate expander when a direct label match fails.
      </p>

      <Toolbar>
        <div style={{ minWidth: 240 }}>
          <label htmlFor="synonyms-payer-filter">Filter by payer</label>
          <Dropdown
            inputId="synonyms-payer-filter"
            value={filterPayerId}
            options={[{ label: "All payers", value: null }, ...payerOptions]}
            onChange={(e) => setFilterPayerId(e.value)}
            placeholder="All payers"
            filter
          />
        </div>
        <Button
          icon="pi pi-plus"
          label="New synonym"
          className="p-button-raised"
          onClick={openAdd}
          disabled={!accountId}
        />
      </Toolbar>

      {!accountId && (
        <Message severity="info" text="Select an account to manage synonyms." />
      )}

      <DataTable
        value={rows}
        emptyMessage="No synonyms yet for this scope."
        loading={isFetching}
        size="small"
      >
        <Column header="Payer" body={payerCellBody} sortable field="payerId" />
        <Column
          field="sourceServiceLine"
          header="Base rate file label"
          sortable
        />
        <Column field="targetServiceLine" header="Rules sheet label" sortable />
        <Column header="Actions" body={actionsCellBody} />
      </DataTable>

      <Dialog
        header={dialogMode === "add" ? "Add synonym" : "Edit synonym"}
        visible={dialogMode !== null}
        style={{ width: "480px" }}
        modal
        onHide={closeDialog}
        footer={
          <>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={closeDialog}
              disabled={isCreating || isUpdating}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={submit}
              loading={isCreating || isUpdating}
            />
          </>
        }
      >
        <FormRow>
          <label htmlFor="form-payer">Payer</label>
          <Dropdown
            inputId="form-payer"
            value={formPayerId}
            options={payerOptions}
            onChange={(e) => setFormPayerId(e.value)}
            placeholder="Select payer"
            filter
            disabled={dialogMode === "edit"} // payer can't change on edit; uniqueness key
          />
        </FormRow>

        <FormRow>
          <label htmlFor="form-source">
            Base rate file label
            <span
              style={{
                fontWeight: "normal",
                color: "var(--gray-700)",
                marginLeft: "0.5rem",
              }}
            >
              — the exact label as it appears in the Service Line column of your
              rate file
            </span>
          </label>
          <InputText
            id="form-source"
            value={formSource}
            onChange={(e) => setFormSource(e.target.value)}
            placeholder="e.g. Radiology Diagnostic Imaging"
          />
        </FormRow>

        <FormRow>
          <label htmlFor="form-target">
            Rules sheet label
            <span
              style={{
                fontWeight: "normal",
                color: "var(--gray-700)",
                marginLeft: "0.5rem",
              }}
            >
              — the label as it appears in your multiplier rules sheet
            </span>
          </label>
          <InputText
            id="form-target"
            value={formTarget}
            onChange={(e) => setFormTarget(e.target.value)}
            placeholder="e.g. Diagnostic Imaging"
          />
        </FormRow>

        {formError && <Message severity="error" text={formError} />}
      </Dialog>

      <ConfirmDialog />
    </Wrapper>
  );
}

export default ServiceLineSynonyms;
